/**
 * Sources Section Component
 * 
 * Displays full source details at the bottom of the article.
 * Each source is expandable with accordion-style interaction.
 */

"use client";

import { useState } from "react";
import { ChevronDownIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

export interface Source {
  url: string;
  title: string;
  description: string;
}

interface SourcesSectionProps {
  sources: Source[];
}

export default function SourcesSection({ sources }: SourcesSectionProps) {
  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set());

  if (sources.length === 0) {
    return null;
  }

  const toggleSource = (index: number) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSources(newExpanded);
  };

  return (
    <div className="mt-16 pt-8 border-t border-gray-200">
      <div className="mb-6">
        <h2 className="font-poppins text-2xl font-bold text-gray-900 mb-2">
          Sources & References
        </h2>
        <p className="font-poppins text-sm text-gray-600">
          External materials and data referenced in this article
        </p>
      </div>

      <div className="space-y-3">
        {sources.map((source, index) => {
          const isExpanded = expandedSources.has(index);
          
          return (
            <div
              key={index}
              id={`source-${index + 1}`}
              className="bg-white border border-gray-200 rounded overflow-hidden hover:border-gray-300 transition-colors scroll-mt-24"
            >
              {/* Source Header - Always Visible */}
              <button
                onClick={() => toggleSource(index)}
                className="w-full px-6 py-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
              >
                {/* Number Badge */}
                <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded font-poppins text-sm font-semibold text-gray-900 flex-shrink-0">
                  {index + 1}
                </span>

                {/* Title and URL */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-poppins text-base font-semibold text-gray-900 mb-1">
                    {source.title}
                  </h3>
                  <p className="font-poppins text-xs text-gray-500 truncate">
                    {source.url}
                  </p>
                </div>

                {/* Expand Icon */}
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-6 pb-4 border-t border-gray-100">
                  <div className="pt-4 space-y-3">
                    {/* Description */}
                    <div>
                      <h4 className="font-poppins text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                        Description
                      </h4>
                      <p className="font-poppins text-sm text-gray-700 leading-relaxed">
                        {source.description}
                      </p>
                    </div>

                    {/* Link */}
                    <div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-poppins text-sm font-medium rounded transition-colors"
                      >
                        Visit Source
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

